import {
  ActiveBookingStates,
  Cartridge,
  CartridgeGroup,
  CartridgeGroupBookingState,
  CatalogHeaderContent,
  CatalogItem,
  OfferedOrRequestedPriceRequestStates,
  RequestedBookingStates,
} from '@markant-platform/cartridge/model';
import { CatalogStage } from '@markant-platform/shared/content/model';

export function getHeaderContent({ elements }: CatalogStage): CatalogHeaderContent {
  if (elements.length) {
    const [first] = elements;
    return { serviceName: first.subline, link: first.linkhref.href };
  }

  return { serviceName: '', link: '' };
}

export function convertToCatalogItems(cartridges: Cartridge[], cartridgeGroup: CartridgeGroup[]): CatalogItem[] {
  return cartridgeGroup.reduce((acc: CatalogItem[], curr: CartridgeGroup) => {
    const cartridgeActiveByKey = cartridges.find(item => item.cartridgeGroupKey === curr.key);
    const serviceGroups = cartridges.filter(item => item.cartridgeGroupKey === curr.key);

    // cartridgeGroup
    if (serviceGroups.length === 1) {
      const [serviceGroup] = serviceGroups;
      return [...acc, convertCartridgeDataToCatalogItem(serviceGroup)];
    }

    // ServiceGroup
    if (cartridgeActiveByKey) {
      const serviceGroup: CatalogItem = convertServiceGroupDataToCatalogItem(curr, serviceGroups);

      return [...acc, serviceGroup];
    }

    return acc;
  }, [] as CatalogItem[]);
}

function convertCartridgeDataToCatalogItem({
  id,
  name,
  description,
  icon,
  cartridgeGroupKey,
  bookingState,
  status,
  variants,
  priceRequestState,
  priceRequestType,
}: Cartridge): CatalogItem {
  return {
    id,
    name,
    description,
    icon,
    serviceKey: cartridgeGroupKey,
    isServiceGroup: false,
    serviceGroup: null,
    serviceCartridgeGroup: {
      state: bookingState,
      status,
      variants,
      ...(priceRequestState && { priceRequestState }),
      ...(priceRequestType && { priceRequestType }),
    },
  } as CatalogItem;
}

export function convertServiceGroupDataToCatalogItem(
  cartridgeGroup: CartridgeGroup,
  cartridges: Cartridge[]
): CatalogItem {
  const { id, name, description, icon, key } = cartridgeGroup;

  return {
    id,
    name,
    description,
    icon,
    serviceKey: key,
    isServiceGroup: true,
    serviceCartridgeGroup: null,
    serviceGroup: {
      state: getStatusFromServiceGroup(cartridges),
      numberBooked: cartridges.filter(
        cartridge => cartridge.bookingState && ActiveBookingStates.includes(cartridge.bookingState)
      ).length,
      services: cartridges.map(
        ({ id, name, description, icon, cartridgeGroupKey, bookingState, status, variants }) =>
          ({
            id,
            name,
            description,
            icon,
            serviceKey: cartridgeGroupKey,
            isServiceGroup: false,
            serviceGroup: null,
            serviceCartridgeGroup: {
              ...(bookingState && { state: bookingState }),
              status,
              variants,
            },
          }) as CatalogItem
      ),
    },
  };
}

export function getStatusFromServiceGroup(cartridges: Cartridge[]): CartridgeGroupBookingState {
  const numberOfBooked = cartridges.filter(
    cartridge => cartridge.bookingState && ActiveBookingStates.includes(cartridge.bookingState)
  ).length;

  switch (true) {
    case cartridges.length && numberOfBooked === cartridges.length:
      return 'FULLY_BOOKED';

    case cartridges.some(
      cartridge =>
        (cartridge.bookingState && RequestedBookingStates.includes(cartridge.bookingState)) ||
        (cartridge.priceRequestState && OfferedOrRequestedPriceRequestStates.includes(cartridge.priceRequestState))
    ):
      return 'ONGOING_PROCESS';

    case numberOfBooked > 0:
      return 'PARTIAL_BOOKED';

    default:
      return 'NONE';
  }
}
